/**
 * @file ProductKindMigrationDialog.tsx
 * @description «Проставить вид по категориям» (phase19.22, W22, макет §4).
 *
 * Сервер предлагает вид по названию категории (`assign-preview`), менеджер
 * подтверждает или меняет, затем AlertDialog «Проставить?» → один POST. Вид
 * пишется В КАТЕГОРИЮ — товары наследуют, свой вид товара не меняется
 * (решение пользователя 2026-09-25). Строки без выбора пропускаются.
 *
 * Товары системных категорий вида не получат (у системных категорий его не
 * бывает) — показываем их число отдельной строкой.
 */

import { Loader2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import { Alert } from "../../ui/alert";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../ui/alert-dialog";
import { Button } from "../../ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../../ui/dialog";
import { useNotify } from "../../ui/NotifyProvider";
import { ApiError } from "../../../lib/api-client";
import {
  productKindsApi,
  type AssignPreviewResult,
  type ProductKind,
} from "../../../lib/api-product-kinds";
import { pluralRu } from "../../../lib/plural";
import { MigrationRows } from "./MigrationRows";

interface Props {
  open: boolean;
  kinds: ProductKind[];
  onClose: () => void;
  /** После проведения — перечитать справочник (счётчики считает сервер). */
  onDone: () => void;
}

export function ProductKindMigrationDialog({ open, kinds, onClose, onDone }: Props) {
  const notify = useNotify();
  const [preview, setPreview] = useState<AssignPreviewResult | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [value, setValue] = useState<Record<string, string>>({});
  const [confirming, setConfirming] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setPreview(null);
    setLoadError(null);
    productKindsApi
      .assignPreview()
      .then((res) => {
        setPreview(res);
        // Предложения сервера — стартовый выбор; менеджер их проверяет.
        setValue(Object.fromEntries(res.categories.map((c) => [c.id, c.suggestedKindId ?? ""])));
      })
      .catch((err: unknown) =>
        setLoadError(err instanceof ApiError ? err.message : "Не удалось загрузить категории"),
      );
  }, [open]);

  const kindOptions = useMemo(
    () => kinds.filter((k) => k.isActive).map((k) => ({ value: k.id, label: k.name })),
    [kinds],
  );
  const chosen = (preview?.categories ?? []).filter((c) => value[c.id]);
  const productsChosen = chosen.reduce((sum, c) => sum + c.productsCount, 0);

  async function apply() {
    setSaving(true);
    try {
      const res = await productKindsApi.assignByCategories(
        chosen.map((c) => ({ categoryId: c.id, productKindId: value[c.id] ?? "" })),
      );
      notify.success(
        `Вид проставлен ${res.updatedCategories} ${pluralRu(res.updatedCategories, ["категории", "категориям", "категориям"])}: ` +
          `${res.productsAffected} ${pluralRu(res.productsAffected, ["товар", "товара", "товаров"])}.`,
      );
      setConfirming(false);
      onDone();
    } catch (err) {
      notify.error(err instanceof ApiError ? err.message : "Не удалось проставить вид");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => (!o && !saving ? onClose() : null)}>
      <DialogContent fullScreen className="gap-3 sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>Проставить вид по категориям</DialogTitle>
        </DialogHeader>

        <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto sm:max-h-[60vh]">
          {loadError ? <Alert severity="error">{loadError}</Alert> : null}
          {!preview && !loadError ? (
            <div className="flex justify-center py-10">
              <Loader2 className="size-6 animate-spin text-primary" aria-label="Загрузка" />
            </div>
          ) : null}
          {preview && preview.categories.length === 0 ? (
            <p className="text-sm text-on-surface-variant">
              Категорий с товарами без вида нет — сопоставлять нечего.
            </p>
          ) : null}
          {preview && preview.categories.length > 0 ? (
            <MigrationRows
              rows={preview.categories}
              value={value}
              onChange={(categoryId, kindId) => setValue((v) => ({ ...v, [categoryId]: kindId }))}
              kindOptions={kindOptions}
              disabled={saving}
            />
          ) : null}
        </div>

        {preview ? (
          <div className="flex flex-col gap-1 text-sm">
            {preview.productsInSystemCategories > 0 ? (
              <p className="text-on-surface-variant">
                В системных категориях: {preview.productsInSystemCategories} тов. — останутся
                «не задан», найдёте отбором в «Товарах».
              </p>
            ) : null}
            <p className="text-on-surface">
              Будет проставлено: {chosen.length}{" "}
              {pluralRu(chosen.length, ["категории", "категориям", "категориям"])} (товары
              наследуют, {productsChosen} тов.). Свой вид у товара не меняется.
            </p>
          </div>
        ) : null}

        <DialogFooter>
          <Button variant="ghost" className="min-h-12" onClick={onClose} disabled={saving}>
            Отмена
          </Button>
          <Button
            className="min-h-12"
            disabled={saving || chosen.length === 0}
            onClick={() => setConfirming(true)}
          >
            Проставить {productsChosen} {pluralRu(productsChosen, ["товару", "товарам", "товарам"])}
          </Button>
        </DialogFooter>
      </DialogContent>

      <AlertDialog open={confirming} onOpenChange={(o) => (!o && !saving ? setConfirming(false) : null)}>
        <AlertDialogContent fullScreen className="max-w-md gap-3">
          <AlertDialogHeader>
            <AlertDialogTitle>Проставить вид?</AlertDialogTitle>
            <AlertDialogDescription>
              Вид запишется в {chosen.length}{" "}
              {pluralRu(chosen.length, ["категорию", "категории", "категорий"])}, {productsChosen}{" "}
              {pluralRu(productsChosen, ["товар получит", "товара получат", "товаров получат"])} его
              по наследству. Срок, этикетка и НДС товаров начнут браться из вида.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <Button
              variant="ghost"
              className="min-h-12"
              onClick={() => setConfirming(false)}
              disabled={saving}
            >
              Не сейчас
            </Button>
            <Button className="min-h-12" onClick={() => void apply()} disabled={saving}>
              {saving ? "Проставляем…" : "Проставить"}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
}
